import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { EMITTER_CONSTANTS } from '@/shared/config';
import { useEmitter } from '@/shared/lib/customHooks';
import type { CheckSavePromptProps } from './index';

/**
 * Guards the report page against navigating away while edits are unsaved. Blocks router transitions and browser unload whenever the condition callback reports pending changes, unless editing is disabled or the socket reported a lost connection, remembering the attempted path so confirming the prompt can replay it.
 *
 * @param condition - Predicate evaluated on every navigation attempt; a truthy result means unsaved report edits exist.
 * @param isNotEdit - When true the report is read-only and navigation is never blocked.
 * @returns An object carrying showPrompt plus the handleYes and handleCancel prompt handlers.
 */
const useCheckSavePrompt = ({ condition, isNotEdit }: CheckSavePromptProps) => {
  const history = useHistory();

  const [showPrompt, setShowPrompt] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const isOffline = useRef(false);

  useEffect(() => {
    history.block((transition) => {
      /* if return nothing => unblock history */
      if (condition() && !isOffline.current && !isNotEdit) {
        setCurrentPath(transition.pathname);
        setShowPrompt(true);
        return 'true';
      }
      return undefined;
    });
    return () => {
      history.block(() => {});
    };
  }, [history, condition, isNotEdit]);

  useEffect(() => {
    /**
     * Warns the browser before the tab is closed or reloaded while report edits are pending. Calls preventDefault and sets returnValue when the condition holds and the session is neither offline nor read-only, returning the confirmation text modern browsers ask for.
     *
     * @param event - Browser beforeunload event whose default is cancelled to trigger the confirmation dialog.
     * @returns The confirmation message for the leave dialog, or undefined when navigation may proceed unchallenged.
     */
    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (condition() && !isOffline.current && !isNotEdit) {
        event.preventDefault();
        // Display a confirmation dialog
        event.returnValue = ''; // For older browsers
        return 'Are you sure you want to leave this page?'; // For modern browsers
      }
      return undefined;
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [condition, isNotEdit]);

  useEmitter(
    EMITTER_CONSTANTS.LOST_CONNECTION,
    (msg) => {
      isOffline.current = true;
    },
    [],
  );

  /**
   * Confirms leaving the report despite unsaved changes. Replaces the router block with a no-op and pushes the path captured when the blocked transition was attempted.
   *
   * @returns Nothing.
   */
  const handleYes = () => {
    history.block(() => {});
    history.push(currentPath);
  };

  /**
   * Dismisses the unsaved-changes prompt and keeps the user on the report. Clears showPrompt without unblocking the router, so the pending navigation is abandoned.
   *
   * @returns A promise resolving once the prompt is hidden.
   */
  const handleCancel = async () => {
    setShowPrompt(false);
  };

  return { showPrompt, handleYes, handleCancel };
};

export default useCheckSavePrompt;
